import type {
  Ajustes,
  Asistencia,
  Bitacora,
  Cliente,
  Devolucion,
  Gasto,
  Lote,
  Movimiento,
  Producto,
  Usuario,
  Venta,
} from '../types'

/** Todas las tablas del sistema, en una sola foto.
 *
 *  El volumen de una distribuidora de berries cabe holgadamente en
 *  memoria (miles de filas, no millones), así que la app carga todo al
 *  entrar y trabaja en local. Eso hace que registrar una venta sea
 *  instantáneo aunque la señal en terreno sea mala; la escritura al
 *  backend ocurre después, sin bloquear la pantalla. */
export interface Snapshot {
  usuarios: Usuario[]
  productos: Producto[]
  lotes: Lote[]
  movimientos: Movimiento[]
  clientes: Cliente[]
  ventas: Venta[]
  devoluciones: Devolucion[]
  bitacora: Bitacora[]
  gastos: Gasto[]
  asistencia: Asistencia[]
  ajustes: Ajustes
}

export type Tabla = Exclude<keyof Snapshot, 'ajustes'>

/** Un cambio de stock pedido al backend.
 *
 *  La app NO escribe `productos.stock` como número absoluto. Desde que
 *  el POS del local comparte las tablas `productos` y `movimientos`, la
 *  copia en memoria puede tener horas de atraso, y guardarla entera
 *  borraría en silencio todo lo que el local vendió mientras tanto. En
 *  vez de eso se manda CUÁNTO cambia y la aritmética la hace la base
 *  sobre el valor que tiene la fila en ese momento. */
export interface CambioStock extends Omit<Movimiento, 'cantidad' | 'stockResultante'> {
  /** Cuánto se mueve: positivo suma, negativo resta. Es lo que usan las
   *  ventas, los ingresos, las devoluciones y las anulaciones. */
  cantidad?: number
  /** Conteo físico: el stock queda EXACTAMENTE en este número y el
   *  delta lo calcula la base contra lo que hay guardado. Lo usan
   *  "Ajustar stock" y el campo Stock de la ficha del producto. */
  stockFinal?: number
}

/** Cómo quedaron las cosas después de aplicar los cambios. El store
 *  pisa su copia en memoria con esto, así que si el POS movió el mismo
 *  producto mientras tanto, la pantalla se corrige sola. */
export interface ResultadoStock {
  /** Stock que quedó en la base, por id de producto. */
  stock: Record<string, number>
  /** Los movimientos como quedaron guardados. `cantidad` y `tipo`
   *  pueden no ser los que mandó la app: en un conteo físico los
   *  calcula la base. */
  movimientos: Pick<Movimiento, 'id' | 'productoId' | 'tipo' | 'cantidad' | 'stockResultante'>[]
}

/** La base todavía no tiene una función que esta versión de la app ya
 *  sabe usar —falta correr la última migración de supabase/schema.sql—.
 *  Es un error con salida: el store lo atrapa y hace lo mismo por un
 *  camino más largo, en vez de dejar al vendedor sin poder vender por
 *  una migración pendiente. */
export class FuncionPendienteError extends Error {
  constructor(funcion: string) {
    super(`Falta crear la función ${funcion} en la base de datos (corre supabase/schema.sql).`)
    this.name = 'FuncionPendienteError'
  }
}

/** Contrato mínimo que debe cumplir cualquier backend. La lógica de
 *  negocio vive en el store, no aquí: un adaptador solo guarda y lee. */
export interface Adapter {
  nombre: 'local' | 'supabase'
  /** True cuando el backend está configurado y accesible. */
  disponible(): Promise<boolean>
  cargar(): Promise<Snapshot>
  /** Guarda las filas completas, con una excepción: en `productos`
   *  nunca escribe `stock` ni `reservado` —esas dos columnas son
   *  territorio compartido con el POS y solo se tocan con
   *  `aplicarStock`. */
  guardar<T extends Tabla>(tabla: T, filas: Snapshot[T]): Promise<void>
  /** Aplica cambios de stock y registra sus movimientos en una sola
   *  operación atómica, con el cálculo del lado de la base. */
  aplicarStock(cambios: CambioStock[]): Promise<ResultadoStock>
  /** Registra una venta ENTERA o nada: descuento de stock, la fila de
   *  la venta y los lotes, en una sola transacción.
   *
   *  Antes eran tres escrituras separadas —stock primero, venta después—
   *  y si la segunda fallaba, la primera ya estaba hecha: el 17-09-2026
   *  la misma venta se reintentó cinco veces y descontó 60 frambuesas
   *  sin que quedara ni una venta guardada. Acá, si algo falla, la base
   *  vuelve exactamente a como estaba.
   *
   *  `lotes` son las filas ya descontadas en memoria; el backend puede
   *  usarlas tal cual (local) o recalcular sobre su propio valor a partir
   *  de `venta.lineas[].lotes` (Supabase, que así no se pisa con el POS).
   *
   *  `folio` vuelve distinto del enviado solo si otro dispositivo alcanzó
   *  a usar ese número primero. */
  registrarVenta(
    venta: Venta,
    movimientos: CambioStock[],
    lotes: Lote[],
  ): Promise<ResultadoStock & { folio: string; repetida?: boolean }>
  eliminar(tabla: Tabla, ids: string[]): Promise<void>
  guardarAjustes(ajustes: Ajustes): Promise<void>
  /** Reemplaza todo el contenido. Se usa al restaurar un respaldo.
   *
   *  CUIDADO: borra TODAS las filas de las tablas compartidas antes de
   *  reinsertar. `productos`, `movimientos`, `clientes` y `usuarios`
   *  también los usa el POS del local, así que esto se lleva por
   *  delante lo que el POS haya registrado y no esté en el respaldo.
   *  Nunca ejecutarlo sin confirmación explícita del administrador. */
  reemplazarTodo(snapshot: Snapshot): Promise<void>
}

export const AJUSTES_POR_DEFECTO: Ajustes = {
  id: 'ajustes',
  stockMinimoGlobal: 10,
  diasAvisoVencimiento: 7,
  diasCredito: 30,
  nombreEmpresa: 'NovaBerry',
  rutEmpresa: '',
  direccionEmpresa: '',
  telefonoEmpresa: '',
  emailEmpresa: '',
  moneda: 'CLP',
  porcentajeIva: 19,
  categoriasGasto: ['Arriendo', 'Sueldos', 'Combustible', 'Publicidad', 'Servicios básicos', 'Otros'],
  bodegaDireccion: 'Maroto 458, Concón',
  bodegaLat: -32.9214426,
  bodegaLng: -71.5159928,
  despachadorNombre: 'Máximo Díaz',
  despachadorTelefono: '+56932584344',
  tarifaDespachoBase: 2000,
  tarifaDespachoPorKm: 700,
  tarifaTraspasoSucursal: 8000,
}

export const snapshotVacio = (): Snapshot => ({
  usuarios: [],
  productos: [],
  lotes: [],
  movimientos: [],
  clientes: [],
  ventas: [],
  devoluciones: [],
  bitacora: [],
  gastos: [],
  asistencia: [],
  ajustes: { ...AJUSTES_POR_DEFECTO },
})
